import React, { useContext, useEffect, useState } from "react";
import {
   Alert as ReactstrapAlert, Button, ButtonGroup, Card, CardBody, CardFooter, Col, Collapse,
   Form, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from "reactstrap";
import Dropzone from "react-dropzone";
import { toast } from "react-toastify";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import _ from "lodash";
import { ServiceThreadSpinner } from ".";
import { api, constants, UserContext, dateHelpers } from "../utils";
import axios from "axios";

export default function AttachmentManager(props) {
   const cancelToken = axios.CancelToken;
   const source = cancelToken.source();
   const userCtx = useContext(UserContext);
   const [fileList, setFileList] = useState([]);
   const [fileToDelete, setFileToDelete] = useState(null);
   const [uploadCollapse, setUploadCollapse] = useState(false);
   const [uploadFiles, setUploadFiles] = useState([]);
   const [validationMessage, setValidationMessage] = useState(null);
   const [working, setWorking] = useState(false);
   let modalClassName = userCtx.theme ? `${userCtx.theme}` : "lightTheme";

   useEffect(() => {
      if (_.isUndefined(props.attachmentKey)) return;
      setWorking(true);
      refreshData();

      return () => {
         source.cancel("axios request cancelled");
      };
      // below line stops the "React Hook useEffect has missing dependencies" eslint error
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.attachmentKey]);

   function deleteFile() {
      api
         .fetch(
            `Attachment/Delete?attachmentTypeId=${props.attachmentTypeId}&attachmentId=${fileToDelete.attachmentId}`
         )
         .then((response) => {
            if (response.data.success) {
               toast.success("File successfully deleted!");
               refreshData();
            } else {
               if (_.isString(response?.data?.message)) {
                  toast.error(response?.data?.message);
               }
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setFileToDelete(null);
            refreshData(source);
         });
   }

   function downloadFile(fileId) {
      api
         .postGetBlob(
            `Attachment/Download?attachmentTypeId=${props.attachmentTypeId}&attachmentId=${fileId}`
         )
         .then((response) => {
            if (response.data) {
               const fileName = response.headers["content-disposition"]
                  .match(/filename=([\s\S]*);/)[1]
                  .replaceAll('"', "");
               if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                  window.navigator.msSaveOrOpenBlob(response.data, fileName);
               } else {
                  const file = new Blob([response.data], {
                     type: response.data.type,
                  });
                  var anchor = window.document.createElement("a");
                  anchor.download = fileName;
                  anchor.href = (window.webkitURL || window.URL).createObjectURL(
                     file
                  );
                  anchor.dataset.downloadurl = [
                     response.data.type,
                     anchor.download,
                     anchor.href,
                  ].join(":");
                  anchor.click();
               }
            } else {
               toast.error(response.message || "Error downloading csv");
            }
         })
         .catch((error) => toast.error(error));
   }

   function onFileDrop(file) {
      _.set(file, "documentTypeId", null);
      _.set(file, "notes", "");

      // Single-file upload policy (CEO directive 2026-05-14). Replace any previously
      // staged file rather than pushing -- the most recent drop wins.
      setUploadFiles([file]);
   }

   function postFiles() {
      setWorking(true);
      setUploadFiles([]);
      setUploadCollapse(false);

      toast.warning("Uploading file... please wait a moment");

      const payload = new FormData();
      _.forEach(uploadFiles, (file) => {
         payload.append("file", file);
         payload.append("documentTypeId", file.documentTypeId);
         // if no notes associated, attach a single space. falsy values do not
         // get appended to the list correctly causing data mismatch server-side
         payload.append("notes", file.notes.length ? file.notes : " ");
      });
      payload.append("attachmentTypeId", props.attachmentTypeId);
      payload.append("attachmentKey", props.attachmentKey);

      api
         .post_form_data("Attachment/upload", payload)
         .then((response) => {
            if (response.data.success) {
               toast.success("File successfully uploaded!");
               refreshData();
            } else {
               if (_.isString(response?.data?.message)) {
                  toast.error(response?.data?.message);
               }
            }
         })
         .catch(api.catchHandler)
         .finally(() => {
            setWorking(false);
            refreshData(source);
         });
   }

   const refreshData = () => {
      api
         .fetchS(
            `Attachment/GetList?attachmentTypeId=${props.attachmentTypeId
            }&attachmentKey=${encodeURIComponent(props.attachmentKey)}`,
            {},
            source.token
         )
         .then((response) => {
            if (axios.isCancel(response)) {
               console.warn("axios request cancelled");
            } else if (response.data?.success) {
               setFileList(response.data.files);
            } else {
               toast.error("Error retrieving Attachments");
            }
         })
         .catch(api.catchHandler)
         .finally(() => setWorking(false));
   };

   function removeUploadFile(index) {
      setUploadFiles(_.without(uploadFiles, uploadFiles[index]));
   }

   function updateUploadFile(index, property, value) {
      const uploadCopy = _.slice(uploadFiles);
      uploadCopy[index][property] = value;
      setUploadFiles(uploadCopy);
   }

   function validateUploadFiles() {
      let errors = 0;
      _.forEach(uploadFiles, (file) => {
         if (file.documentTypeId === null) {
            errors++;
         }
      });
      if (errors) {
         setValidationMessage("Document Type required");
      } else {
         setValidationMessage(null);
         postFiles();
      }
   }

   return (
      <>
         <Row className="lineheight15">&nbsp;</Row>
         <span className="h2" children="Attachments" /> <span>&nbsp;</span>{" "}
         <span
            className={props.AttachmentMutedText || "text-muted"}
            children={`Attachments for ${_.startCase(
               _.findKey(
                  constants.ATTACHMENT_TYPES,
                  (v) => v === props.attachmentTypeId
               )
            )} ${props.contextName}`}
         />
         {userCtx.currentUser && userCtx.currentUser.isEditRole && (
            <Button
               children="Upload Attachment"
               disabled={working}
               className={`${props.fullScreen ? "me-6" : ""} shredButton float-end`}
               onClick={() => setUploadCollapse(!uploadCollapse)}
            />
         )}
         <hr />
         <Collapse id="file-uploader" isOpen={uploadCollapse} style={{ color: "black" }} >
            <Card>
               <CardBody>
                  {validationMessage && (
                     <ReactstrapAlert color="danger" children={validationMessage} />
                  )}
                  <Dropzone
                     multiple={false}
                     onDrop={(acceptedFiles, fileRejections) => {
                        // With multiple={false}, react-dropzone puts all dropped files into
                        // fileRejections (and leaves acceptedFiles empty) when a user drops
                        // more than one. Surface that explicitly so users get feedback
                        // instead of a silent no-op.
                        if (fileRejections && fileRejections.length > 0) {
                           toast.warning("Only one file may be uploaded at a time.");
                           return;
                        }
                        if (acceptedFiles && acceptedFiles.length > 0)
                           onFileDrop(acceptedFiles[0]);
                     }}
                  >
                     {({ getRootProps, getInputProps }) => (
                        <div {...getRootProps()} className="text-center">
                           <input {...getInputProps()} />
                           <p>Drag 'n' drop attachment here, or click to select</p>
                           <FontAwesomeIcon icon="file-upload" size="3x" className="mb-4" />
                           <Row className="pt-3">
                              <Col xs="3" children="Document Type" />
                              <Col xs="3" children="Notes" />
                              <Col xs="5" children="File Name" />
                              <Col xs="1" children="Remove" />
                           </Row>
                           <Form>
                              {_.map(uploadFiles, (file, index) => {
                                 return (
                                    <Row
                                       key={index}
                                       onClick={(e) => e.stopPropagation()}
                                       className={`py-2 ${index % 2 === 0 ? "bg-white" : "bg-light"}`}
                                    >
                                       <Col xs="3">
                                          <Select
                                             classNamePrefix="serviceThreadSelect"
                                             options={_.map(
                                                constants.DOCUMENT_TYPES,
                                                (value, key) => {
                                                   return {
                                                      label: _.startCase(key),
                                                      value: value,
                                                   };
                                                }
                                             )}
                                             setValue={() => file.documentTypeId}
                                             onChange={(e) =>
                                                updateUploadFile(
                                                   index,
                                                   "documentTypeId",
                                                   e.value
                                                )
                                             }
                                          />
                                       </Col>
                                       <Col xs="3">
                                          <Input
                                             className="serviceThreadTextArea"
                                             type="textarea"
                                             value={file.notes}
                                             onChange={(e) => updateUploadFile(index, "notes", e.target.value) }
                                          />
                                       </Col>
                                       <Col xs="5" children={file.name} />
                                       <Col xs="1">
                                          <span
                                             title="Remove this upload"
                                             className="btn btn-sm btn-danger float-end"
                                             onClick={(e) => {
                                                removeUploadFile(index);
                                                e.stopPropagation();
                                             }}
                                          >
                                             <FontAwesomeIcon icon="times-circle" />
                                          </span>
                                       </Col>
                                    </Row>
                                 );
                              })}
                           </Form>
                        </div>
                     )}
                  </Dropzone>
               </CardBody>
               <CardFooter style={{ backgroundColor: "#fff" }}>
                  {uploadFiles.length === 0 ? null : (
                     <ButtonGroup>
                        <Button
                           children="Upload"
                           className="float-end me-2"
                           disabled={uploadFiles.length === 0}
                           color="success"
                           onClick={() => { validateUploadFiles(); }}
                        />
                        <Button
                           children="Cancel"
                           className="float-end"
                           onClick={() => {
                              setUploadCollapse(false);
                              setUploadFiles([]);
                              setValidationMessage(null);
                           }}
                        />
                     </ButtonGroup>
                  )}
               </CardFooter>
            </Card>
         </Collapse>
         <ServiceThreadSpinner on={working}>
            {fileList && fileList.length !== 0 ? (
               <Table size="sm" className="data-table">
                  <thead className="table-header">
                     <tr>
                        <th style={{ width: "45%" }}>File Name</th>
                        <th style={{ width: "20%" }}>Notes</th>
                        <th style={{ width: "15%" }}>Uploaded By</th>
                        <th style={{ width: "15%" }}>Upload Date</th>
                        {userCtx.currentUser && userCtx.currentUser.isEditRole && (
                           <th style={{ width: "5%" }}></th>
                        )}
                     </tr>
                  </thead>
                  <tbody>
                     {_.map(fileList, (file, index) => {
                        return (
                           <tr
                              key={index}
                              className={`tr-${index % 2 === 0 ? "primary" : "secondary"}`}
                           >
                              <td className="data-cell">
                                 <span
                                    children={file.fileName}
                                    className="shredLink fw-bold transparent-background"
                                    onClick={() => downloadFile(file.attachmentId)}
                                 />
                              </td>
                              <td className="data-cell" children={file.notes} />
                              <td className="data-cell" children={file.uploadedBy} />
                              <td className="data-cell" children={dateHelpers.toLongDateString(file.uploadDate)} />
                              {userCtx.currentUser && userCtx.currentUser.isEditRole && (
                                 <td>
                                    <span
                                       children="Delete"
                                       className="shredLink fw-bold transparent-background"
                                       onClick={() => setFileToDelete(file)}
                                    />
                                 </td>
                              )}
                           </tr>
                        );
                     })}
                  </tbody>
                  <tfoot className="table-footer">
                     <tr>
                        <td colSpan="5" />
                     </tr>
                  </tfoot>
               </Table>
            ) : (
               <div>
                  <span>No attachments found for this item</span>
               </div>
            )}
         </ServiceThreadSpinner>
         <Modal
            id="confirm-delete"
            isOpen={!_.isNil(fileToDelete)}
            contentClassName={modalClassName}
         >
            <ModalHeader children="Confirm Delete" />
            <ModalBody>
               Are you sure you want to delete the file
               <br />
               <strong>{fileToDelete && fileToDelete.fileName}</strong>?
            </ModalBody>
            <ModalFooter>
               <ButtonGroup>
                  <Button children="Yes" className="button-success" onClick={() => deleteFile()} />
                  <Button children="No" className="button-danger" onClick={() => setFileToDelete(null)} />
               </ButtonGroup>
            </ModalFooter>
         </Modal>
      </>
   );
}
